import React from 'react';
import { ProjectTypeDefinition } from '../../types/project-types';
import { Project } from '../../types';

interface ReviewStepProps {
  definition: ProjectTypeDefinition;
  formData: Record<string, any>;
  project?: Partial<Project>;
  onComplete: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  definition,
  formData,
  project,
  onComplete
}) => {
  const getFieldValue = (key: string) => {
    const value = formData[key];
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '-';
  };

  const getTypeIcon = () => {
    const icons = {
      learning_goal: '🎓',
      project_goal: '🎯',
      personal_goal: '💪',
      vision_goal: '🔮',
      technical_goal: '⚙️'
    };
    return icons[definition.category as keyof typeof icons] || '📋';
  };

  const formatAIPrompt = () => {
    let prompt = definition.aiPromptTemplate;
    
    // Replace placeholders with actual values
    prompt = prompt.replace('{description}', project?.description || 'Kein Projektbeschreibung');
    
    definition.requiredFields.forEach(field => {
      const placeholder = `{${field.key}}`;
      const value = formData[field.key] || 'Nicht angegeben';
      prompt = prompt.replace(new RegExp(placeholder, 'g'), String(value));
    });
    
    return prompt;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Konfiguration überprüfen
        </h3>
        <p className="text-gray-600">
          Überprüfe deine Projekttyp-Konfiguration bevor sie angewendet wird.
        </p>
      </div>

      {/* Project Overview */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-3xl">{getTypeIcon()}</span>
          <div>
            <h4 className="text-xl font-semibold text-gray-900">
              {definition.label}
            </h4>
            <p className="text-gray-600">{definition.description}</p>
          </div>
        </div>
        
        {project?.name && (
          <div className="border-t border-gray-200 pt-4">
            <h5 className="font-medium text-gray-900 mb-2">Projekt:</h5>
            <p className="text-gray-700 font-medium">{project.name}</p>
            {project.description && (
              <p className="text-gray-600 text-sm mt-1">"{project.description}"</p>
            )}
          </div>
        )}
      </div>

      {/* Type Characteristics */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-4">📋 Planungsansatz</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h5 className="font-medium text-blue-800 mb-2">Trigger-Typ</h5>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-blue-700 capitalize">
                {definition.characteristics.triggerType}
              </span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              {definition.characteristics.triggerType === 'deadline' && 'Feste Termine bestimmen Planung'}
              {definition.characteristics.triggerType === 'outcome' && 'Lernergebnisse bestimmen Planung'}  
              {definition.characteristics.triggerType === 'milestone' && 'Etappenziele bestimmen Planung'}
              {definition.characteristics.triggerType === 'vision' && 'Zukunftsbild bestimmt Planung'}
              {definition.characteristics.triggerType === 'result' && 'Gewünschtes Ergebnis bestimmt Planung'}
            </p>
          </div>
          
          <div>
            <h5 className="font-medium text-blue-800 mb-2">Zeithorizont</h5>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-blue-700 capitalize">
                {definition.characteristics.timeHorizon}
              </span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              {definition.characteristics.timeHorizon === 'short' && '< 6 Monate'}
              {definition.characteristics.timeHorizon === 'medium' && '6-18 Monate'}
              {definition.characteristics.timeHorizon === 'long' && '> 18 Monate'}
              {definition.characteristics.timeHorizon === 'indefinite' && 'Ohne festes Ende'}
            </p>
          </div>
          
          <div>
            <h5 className="font-medium text-blue-800 mb-2">Erfolgsmessung</h5>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-blue-700 capitalize">
                {definition.characteristics.measurementFocus}
              </span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              {definition.characteristics.measurementFocus === 'completion' && 'Abschluss von Tasks'}
              {definition.characteristics.measurementFocus === 'learning' && 'Kompetenzerwerb'}
              {definition.characteristics.measurementFocus === 'performance' && 'Leistungsverbesserung'}
              {definition.characteristics.measurementFocus === 'transformation' && 'Strukturelle Veränderung'}
              {definition.characteristics.measurementFocus === 'functionality' && 'Technische Funktion'}
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">⚙️ Konfiguration</h4>
        <div className="space-y-4">
          {definition.requiredFields.map((field) => (
            <div key={field.key} className="flex justify-between items-start">
              <div className="flex-1">
                <h5 className="font-medium text-gray-800">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </h5>
                <p className="text-sm text-gray-600 mt-1">
                  Typ: {field.type} 
                  {field.options && ` | Optionen: ${field.options.length}`}
                </p>
              </div>
              <div className="flex-1 text-right">
                <p className="text-gray-900 font-medium">
                  {getFieldValue(field.key)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Planning Steps */}
      <div className="bg-green-50 rounded-lg p-6">
        <h4 className="font-semibold text-green-900 mb-4">📝 Planungsschritte</h4>
        <p className="text-green-700 text-sm mb-4">
          Diese Schritte werden nach der Aktivierung für dein Projekt verfügbar:
        </p>
        <div className="space-y-3">
          {definition.planningSteps.map((step, index) => (
            <div key={step.order} className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-800 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold">{index + 1}</span>
              </div>
              <div className="flex-1">
                <h5 className="font-medium text-green-900">{step.name}</h5>
                <p className="text-sm text-green-700 mt-1">{step.description}</p>
                <div className="flex items-center space-x-3 mt-2">
                  {step.isRequired && (
                    <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                      Erforderlich
                    </span>
                  )}
                  {step.defaultDuration && (
                    <span className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                      ~{step.defaultDuration} Tage
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Expected Artifacts */}
      <div className="bg-purple-50 rounded-lg p-6">
        <h4 className="font-semibold text-purple-900 mb-4">📦 Zu erstellende Artefakte</h4>
        <p className="text-purple-700 text-sm mb-4">
          Diese Dokumente und Strukturen werden während der Planung erstellt:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {definition.artifactTypes.map((artifact) => (
            <div key={artifact.name} className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded bg-purple-100 text-purple-800 flex items-center justify-center flex-shrink-0">
                <span className="text-xs">📄</span>
              </div>
              <div className="flex-1">
                <h5 className="font-medium text-purple-900">{artifact.name}</h5>
                <p className="text-sm text-purple-700 mt-1">{artifact.description}</p>
                {artifact.isRequired && (
                  <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-700 rounded mt-2">
                    Erforderlich
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Integration Preview */}
      <div className="bg-indigo-50 rounded-lg p-6">
        <h4 className="font-semibold text-indigo-900 mb-4">🤖 AI-Integration</h4>
        <p className="text-indigo-700 text-sm mb-4">
          Die AI wird mit diesem optimierten Prompt für deinen Projekttyp arbeiten:
        </p>
        <div className="bg-white border border-indigo-200 rounded-lg p-4">
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
            {formatAIPrompt()}
          </pre>
        </div>
        <p className="text-indigo-600 text-xs mt-2">
          💡 Der Prompt wird automatisch mit deinen Konfigurationsdaten gefüllt und 
          sorgt für präzise, typspezifische Planungsergebnisse.
        </p>
      </div>

      {/* Final Confirmation */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
          </svg>
          <div>
            <h4 className="font-semibold text-yellow-900 mb-2">
              ⚠️ Wichtiger Hinweis
            </h4>
            <p className="text-yellow-800 text-sm">
              Nach der Aktivierung wird dieser Projekttyp die Art beeinflussen, wie AI-Dekomposition, 
              Zielsetzung und Tracking funktionieren. Du kannst den Typ später in den Projekteinstellungen 
              ändern, aber bereits erstellte Planungsschritte bleiben erhalten.
            </p>
          </div>
        </div>
      </div>

      {/* Success Action */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
            </svg>
            <div>
              <h4 className="font-semibold text-green-900 mb-1">
                Bereit für die Aktivierung! 🚀
              </h4>
              <p className="text-green-800 text-sm">
                Deine Projekttyp-Konfiguration ist vollständig und wird nach der 
                Bestätigung aktiviert. Der Projekttyp-Assistent wird geschlossen und 
                dein Projekt wird entsprechend konfiguriert.
              </p>
            </div>
          </div>
          <button
            onClick={onComplete}
            className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Aktivieren
          </button>
        </div>
      </div>
    </div>
  );
};